import axios from 'axios'

/**
 * Turn a failed request into a sentence a human reads.
 *
 * The caller's `fallback` is a guarantee, not a suggestion: given a non-empty
 * `fallback`, this never returns text that renders as nothing. That matters
 * more than it sounds, because every template that shows one of these guards
 * it with `v-if` — and an empty string is falsy, so returning one does not
 * produce a blank message, it removes the element entirely. A failed action
 * would then look to the operator exactly like nothing having happened.
 *
 * Four routes used to end there, all reachable from a real backend: a `detail`
 * that is itself `''`, one that is only whitespace (truthy, so it rendered an
 * empty box — same symptom, different road), a `detail: []` whose join
 * produces nothing, and an `AxiosError` with an empty `message` (it is an
 * `Error`, so the last branch claimed it before the fallback could). All four
 * are pinned in the spec.
 */
export function extractApiError(e: unknown, fallback: string): string {
  // Both checks live HERE rather than at each `return` below, so a branch
  // added later cannot reintroduce the hole by forgetting to repeat them.
  //
  // `||` rather than `=== ''` on purpose: `JSON.stringify` is typed `string`
  // but returns `undefined` for an `undefined` input, so a future branch
  // returning its result unjoined would slip a real `undefined` past an
  // equality check and into the template. `||` catches that too.
  return unwrap(e).trim() || fallback
}

/**
 * The shape-sniffing half: what the server said, or `''` if it said nothing
 * usable. Never chooses the fallback itself — that is the caller's job above,
 * and keeping the two apart is what makes the guarantee hold for every branch.
 */
function unwrap(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const detail = e.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // Pydantic ValidationError list: [{loc, msg, type, ...}]
      return detail
        .map((item: unknown) => {
          if (item && typeof item === 'object' && 'msg' in item) {
            const obj = item as { loc?: unknown[]; msg?: string }
            const loc = Array.isArray(obj.loc) ? obj.loc.join('.') : ''
            return loc ? `${loc}: ${obj.msg}` : String(obj.msg)
          }
          return JSON.stringify(item)
        })
        .join('; ')
    }
    if (detail && typeof detail === 'object') return JSON.stringify(detail)
    if (e.message) return e.message
  }
  if (e instanceof Error) return e.message
  return ''
}
